import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  MenuItem as MuiMenuItem,
  Skeleton,
  Checkbox,
} from '@mui/material';
import {
  Add,
  Refresh,
  Delete,
  ShoppingCart,
  Search,
  Person,
  Link as LinkIcon,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Close,
  ShoppingBag,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import {
  addItemToUserCart,
  getAdminCartItems,
  fetchItemDetails,
  checkTranslationStatus,
  updateAdminCartSkuQuantity,
  deleteAdminCartItem,
} from '../../services/cartService';
import { getCurrencyRates } from '../../services/currency';
import { settingsService } from '../../services/settingsService';

/**
 * Currency conversion utility
 * (from frontend/src/utils/currency.js)
 */
const currency_converter = (defaultCurrency, preferredCurrency, price, rate) => {
  if (defaultCurrency === preferredCurrency) {
    return price;
  }
  
  if (defaultCurrency === 'NGN') {
    return price / rate;
  }
  
  // Converting from defaultCurrency (CNY) to preferredCurrency (NGN)
  return price * rate;
};

/**
 * Round number to 2 decimal places and format with locale
 * (from frontend/src/utils/roundtotwo.js)
 */
const round_to_two = (number) => {
  if (number == null || isNaN(number) || !isFinite(number)) {
    return '0.00';
  }
  
  const result = +(Math.round(Number(number) * 100) / 100).toFixed(2);
  return result.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2});
};

/**
 * Format properties name to extract readable spec values
 * (from frontend/src/utils/helpers.js)
 */
const formatPropertiesName = (propertiesName) => {
  if (!propertiesName || typeof propertiesName !== 'string') {
    return '';
  }

  // Split by semicolon to get individual property parts
  const parts = propertiesName.split(';').map(part => part.trim()).filter(part => part);
  
  // Determine which part to process:
  // - For 2-spec items: process the second spec (starts with "1:")
  // - For 1-spec items: process the first (and only) spec (starts with "0:")
  let targetParts = [];
  
  if (parts.length === 1) {
    // 1-spec item: process the only part (starts with "0:")
    targetParts = parts;
  } else {
    // 2-spec item: filter for second spec (starts with "1:")
    targetParts = parts.filter(part => {
      const firstColonIndex = part.indexOf(':');
      if (firstColonIndex === -1) return false;
      const propIndex = part.substring(0, firstColonIndex);
      return propIndex === '1'; // Only process second spec (starts with "1:")
    });
  }
  
  // If no target part found, return empty
  if (targetParts.length === 0) {
    return '';
  }
  
  const formattedParts = targetParts.map(part => {
    // Format: "prop_index:value_index:prop_name:prop_value"
    // The prop_value may or may not contain colons
    // Pattern: ^(\d+):(\d+):([^:]+):(.+)$
    // This matches: prop_index:value_index:prop_name:prop_value
    // Where prop_value can contain any characters (with or without colons)
    
    const match = part.match(/^(\d+):(\d+):([^:]+):(.+)$/);
    if (match) {
      // match[4] is the prop_value (everything after the third colon)
      return match[4].trim();
    }
    
    // Fallback: if regex doesn't match, try to find the third colon manually
    // This handles cases where the format might be slightly different
    let colonCount = 0;
    let thirdColonIndex = -1;
    for (let i = 0; i < part.length; i++) {
      if (part[i] === ':') {
        colonCount++;
        if (colonCount === 3) {
          thirdColonIndex = i;
          break;
        }
      }
    }
    
    if (thirdColonIndex !== -1) {
      // Extract everything after the third colon
      return part.substring(thirdColonIndex + 1).trim();
    }
    
    // If no third colon found, check if it's format "0:0: Color" or "1:3: Color" (no value after third colon)
    // This shouldn't happen in normal cases, but handle it just in case
    const twoColonMatch = part.match(/^(\d+):(\d+):(.+)$/);
    if (twoColonMatch) {
      // If there are only 2 colons, return the part after the second colon
      return twoColonMatch[3].trim();
    }
    
    // Return as-is if format is unexpected
    return part;
  });

  return formattedParts.length > 0 ? formattedParts[0] : '';
};

/**
 * Calculate discount price based on priceRange and total quantity
 * (from frontend/src/utils/pricingUtils.js)
 */
const calculateDiscountPrice = (priceRange, totalQuantity, basePrice) => {
  const noDiscountReturn = {
    hasDiscount: false,
    discountPrice: 0,
    originalPrice: basePrice,
    amountSaved: 0,
    discountPercentage: 0
  };

  if (!priceRange || !Array.isArray(priceRange) || priceRange.length === 0) {
    return noDiscountReturn;
  }

  if (priceRange.length === 1) {
    const [quantity, price] = priceRange[0];
    if (totalQuantity >= parseInt(quantity)) {
      const discountPrice = parseFloat(price);
      const amountSaved = (basePrice - discountPrice) * totalQuantity;
      const discountPercentage = ((basePrice - discountPrice) / basePrice) * 100;

      return {
        hasDiscount: discountPrice < basePrice,
        discountPrice: discountPrice < basePrice ? discountPrice : 0,
        originalPrice: basePrice,
        amountSaved: discountPrice < basePrice ? amountSaved : 0,
        discountPercentage: discountPrice < basePrice ? discountPercentage : 0
      };
    }
    return noDiscountReturn;
  }

  if (priceRange.length === 2) {
    const quantity1 = priceRange[0][0];
    const quantity2 = priceRange[1][0];
    if (parseInt(quantity1) === parseInt(quantity2)) {
      return noDiscountReturn;
    }
  }

  const sortedPriceRange = [...priceRange].sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

  let applicablePrice = basePrice;
  for (let i = sortedPriceRange.length - 1; i >= 0; i--) {
    const [quantity, price] = sortedPriceRange[i];
    if (totalQuantity >= parseInt(quantity)) {
      applicablePrice = parseFloat(price);
      break;
    }
  }

  const hasDiscount = applicablePrice < basePrice;
  const amountSaved = hasDiscount ? (basePrice - applicablePrice) * totalQuantity : 0;
  const discountPercentage = hasDiscount ? ((basePrice - applicablePrice) / basePrice) * 100 : 0;

  return {
    hasDiscount,
    discountPrice: hasDiscount ? applicablePrice : 0,
    originalPrice: basePrice,
    amountSaved,
    discountPercentage
  };
};

const getItemDiscountInfo = (item, totalQuantity) => {
  const priceRange = item.priceRange || [];
  const basePrice = priceRange.length > 0 ? parseFloat(priceRange[0][1]) : 0;
  return calculateDiscountPrice(priceRange, totalQuantity, basePrice);
};

/**
 * Extract offer ID from various 1688 URL formats and checkstall.com URLs
 */
const extractOfferId = (url) => {
  const trimmedUrl = url.trim().replace(/"/g, '');
  
  // Check for checkstall.com URLs first (format: https://checkstall.com/share/pid-919962455861)
  if (trimmedUrl.includes('checkstall.com/share/pid-')) {
    const match = trimmedUrl.match(/checkstall\.com\/share\/pid-(\d+)/i);
    return match ? match[1] : null;
  }
  
  // Check for checkstall.com URLs with query parameters (format: https://checkstall.com/share/pid-919962455861?param=value)
  if (trimmedUrl.includes('checkstall.com/share/pid-')) {
    const match = trimmedUrl.match(/checkstall\.com\/share\/pid-(\d+)/i);
    return match ? match[1] : null;
  }
  
  if (trimmedUrl.toLowerCase().includes('offerid=')) {
    const match = trimmedUrl.match(/offerId=(\d+)/i);
    return match ? match[1] : null;
  }
  
  if (trimmedUrl.includes('num_iid=')) {
    const match = trimmedUrl.match(/num_iid=(\d+)/);
    return match ? match[1] : null;
  }
  
  if (trimmedUrl.includes('1688.com/offer/')) {
    const match = trimmedUrl.match(/1688\.com\/offer\/(\d+)/);
    return match ? match[1] : null;
  }
  
  if (trimmedUrl.includes('detail.m.1688.com') && trimmedUrl.toLowerCase().includes('offerid=')) {
    const match = trimmedUrl.match(/offerId=(\d+)/i);
    return match ? match[1] : null;
  }
  
  return null;
};




// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const Cart = () => {
  const { t } = useTranslation(['cart', 'common']);
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [userFilter, setUserFilter] = useState('');
  const debouncedUserFilter = useDebounce(userFilter, 500);
  
  // Multi-select state
  const [selectedCartItems, setSelectedCartItems] = useState([]);
  
  // View SKUs dialog state
  const [viewSkusDialog, setViewSkusDialog] = useState(false);
  const [selectedCartItemForSkus, setSelectedCartItemForSkus] = useState(null);
  const [skuQuantities, setSkuQuantities] = useState({});
  const [updatingSkuId, setUpdatingSkuId] = useState(null);
  const [deletingCartId, setDeletingCartId] = useState(null);
  
  // Add item dialog states
  const [addDialog, setAddDialog] = useState(false);
  const [productLink, setProductLink] = useState('');
  const [extractedItemNumber, setExtractedItemNumber] = useState('');
  const [itemData, setItemData] = useState(null);
  const [loadingItem, setLoadingItem] = useState(false);
  const [itemError, setItemError] = useState(null);
  
  // Item specifications
  const [selectedSKUs, setSelectedSKUs] = useState({});
  const [selectedProperties, setSelectedProperties] = useState({});
  const [selectedFirstSpec, setSelectedFirstSpec] = useState(null); // For 2-spec items
  const [property, setProperty] = useState('0:0');
  const [imageIndex, setImageIndex] = useState(0);


  const handleVariationClick = (index, val) => {
    setImageIndex(index);
    if (val) {
      // For 2-spec items: update property and selectedFirstSpec
      setProperty(val);
      // Find the spec value from props_list to set selectedFirstSpec
      if (itemData?.props_list?.[val]) {
        const specValue = itemData.props_list[val].split(':')[1] || itemData.props_list[val];
        setSelectedFirstSpec(specValue);
      }
    }
    // For 1-spec items: only imageIndex is updated (val is undefined)
  };
  
  // Memoize SKU data (following AccessoriesList pattern)
  const sku = useMemo(() => {
    // Handle both structures: itemData.skus.sku (array) or itemData.sku (object)
    let skuArray = [];
    if (itemData?.skus?.sku && Array.isArray(itemData.skus.sku)) {
      skuArray = itemData.skus.sku;
    } else if (itemData?.sku) {
      // If sku is an object with keys, convert to array
      if (typeof itemData.sku === 'object' && !Array.isArray(itemData.sku)) {
        skuArray = Object.values(itemData.sku);
      } else if (Array.isArray(itemData.sku)) {
        skuArray = itemData.sku;
      }
    }
    
    if (!skuArray || skuArray.length === 0) {
      return [];
    }
    
    const haveStockItems = skuArray.filter(item => (item.quantity || 0) > 0);
    const outOfStockItems = skuArray.filter(item => (item.quantity || 0) === 0);
    return [...haveStockItems, ...outOfStockItems];
  }, [itemData?.skus?.sku, itemData?.sku]);
  
  // Helper function to get SKU image URL from propsImg
  const getSkuImageUrl = (properties) => {
    if (!itemData || !properties) return itemData?.pic_url || '';
    
    const propsImg = itemData?.prop_imgs?.prop_img || [];
    // For 2-spec items, properties is like "0:0;1:3", we need to match the first part
    // For 1-spec items, properties is like "0:0"
    const firstProperty = properties.split(";")[0];
    const matchingImg = propsImg.find(img => img.properties === firstProperty);
    
    return matchingImg?.url || itemData.pic_url || '';
  };

  // Helper function to get SKU image URL by skuId (finds the actual SKU and gets its image)
  const getSkuImageUrlById = (skuId) => {
    if (!itemData || !skuId) return itemData?.pic_url || '';
    
    // Find the SKU in the sku array
    const skuArray = itemData?.skus?.sku || (itemData?.sku ? Object.values(itemData.sku) : []);
    const foundSku = skuArray.find(s => String(s.sku_id || s.id || '') === String(skuId));
    
    if (foundSku && foundSku.properties) {
      return getSkuImageUrl(foundSku.properties);
    }
    
    return itemData?.pic_url || '';
  };

  // Extract second spec options dynamically
  const secondSpecOptions = useMemo(() => {
    const options = [];
    sku.forEach((val, index) => {
      let props_1 = val.properties?.split(";")[0];
      if (props_1 === property) {
        // Use formatPropertiesName to extract the second spec value correctly
        // This handles values that contain colons (e.g., "120码（H:110-120cm）潮流")
        const spec2Value = formatPropertiesName(val.properties_name);
        
        if (spec2Value) {
          // Check if we already have this second spec value (to avoid duplicates)
          const existingOption = options.find(opt => opt.value === spec2Value);
          
          if (!existingOption) {
            // Get image URL for this SKU
            const imageUrl = getSkuImageUrl(val.properties);
            
            options.push({
              value: spec2Value,
              price: parseFloat(val.price) || 0,
              stock: val.quantity || 0,
              skuId: val.sku_id?.toString() || val.sku_id || '',
              properties: val.properties || '',
              properties_name: val.properties_name || '',
              imageUrl: imageUrl, // Store image URL in option
            });
          } else {
            // If duplicate, combine stock quantities
            // Also ensure imageUrl is set if it wasn't set before
            existingOption.stock += (val.quantity || 0);
            if (!existingOption.imageUrl && val.properties) {
              existingOption.imageUrl = getSkuImageUrl(val.properties);
            }
          }
        }
      }
    });
    return options;
  }, [sku, property]);
  
  // Translation polling
  const [translationTaskId, setTranslationTaskId] = useState(null);
  const [isPollingTranslation, setIsPollingTranslation] = useState(false);
  const [translationPollCount, setTranslationPollCount] = useState(0);
  const MAX_POLL_ATTEMPTS = 30; // 30 attempts * 2 seconds = 60 seconds max
  
  // User search
  const [userIdentifier, setUserIdentifier] = useState('');
  const [userSearchError, setUserSearchError] = useState('');
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [totalItems, setTotalItems] = useState(0);
  const [checkoutPricingSettings, setCheckoutPricingSettings] = useState(null);
  
  // Currency conversion
  const [currencyRate, setCurrencyRate] = useState(null); // { rate, symbol }

  const getCheckoutPricingSettings = useCallback(async () => {
    try {
      const settings = await settingsService.getShippingSettings(true);
      const normalizedSettings = {
        initial_shipping_fee: parseFloat(settings.initial_shipping_fee),
        minimum_service_fee: parseFloat(settings.minimum_service_fee),
        service_fee_percentage: parseFloat(settings.service_fee_percentage),
        price_display_mode: String(settings.price_display_mode || 'separated').trim().toLowerCase(),
        pricing_tier: settings.pricing_tier || [],
        area_id: settings.area_id
      };
      setCheckoutPricingSettings(normalizedSettings);
      return normalizedSettings;
    } catch (error) {
      return null;
    }
  }, []);

  useEffect(() => {
    getCheckoutPricingSettings();
  }, [getCheckoutPricingSettings]);
  
  // Fetch currency rate on mount
  useEffect(() => {
    const fetchCurrencyRate = async () => {
      try {
        const response = await getCurrencyRates({ page: 1, page_size: 100 });
        const rates = response.results || [];
        
        // Find the rate where base is NGN and target is CNY (for converting CNY to NGN)
        const ngnToCnyRate = rates.find(rate => 
          rate.base_currency_code === 'NGN' && rate.target_currency_code === 'CNY'
        );
        
        if (ngnToCnyRate && ngnToCnyRate.base_currency_rate) {
          setCurrencyRate({
            rate: parseFloat(ngnToCnyRate.base_currency_rate),
            symbol: ngnToCnyRate.base_currency_symbol || '₦',
          });
        }
      } catch (error) {
        // Don't set rate if fetch fails - will show CNY prices
      }
    };
    
    fetchCurrencyRate();
  }, []);
  
  // Helper function to format price (converts to NGN if rate available, otherwise shows CNY)
  const formatPrice = (price) => {
    if (!currencyRate || !price) {
      return `¥${parseFloat(price || 0).toFixed(2)}`;
    }
    
    const convertedPrice = currency_converter('CNY', 'NGN', parseFloat(price), currencyRate.rate);
    return `${currencyRate.symbol}${round_to_two(convertedPrice)}`;
  };

  const totalPages = Math.ceil((cartItems.length > 0 ? totalItems : 0) / rowsPerPage) || 1;

  const getSkuArray = useCallback((cartItem) => {
    const skus = cartItem?.sku || cartItem?.skus || [];
    return Array.isArray(skus) ? skus : (skus ? [skus] : []);
  }, []);

  const buildSkuQuantitiesMap = useCallback((cartItem) => {
    return getSkuArray(cartItem).reduce((acc, sku) => {
      const skuKey = sku.id || sku.sku_id;
      if (skuKey !== undefined && skuKey !== null) {
        acc[skuKey] = String(sku.quantity || 1);
      }
      return acc;
    }, {});
  }, [getSkuArray]);

  const loadCartItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
      };
      if (debouncedUserFilter.trim()) {
        params.user = debouncedUserFilter.trim();
      }
      
      const response = await getAdminCartItems(params);
      if (response.status === 'success') {
        setCartItems(response.results || []);
        setTotalItems(response.count || 0);
      }
    } catch (error) {
      showSnackbar(t('cart:messages.loadFailed', 'Failed to load cart items'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedUserFilter, t]);

  useEffect(() => {
    setPage(0);
  }, [debouncedUserFilter, rowsPerPage]);

  useEffect(() => {
    loadCartItems();
  }, [loadCartItems]);
  
  // Clear selection only when filter changes (not on page change)
  useEffect(() => {
    if (debouncedUserFilter) {
      setSelectedCartItems([]);
    }
  }, [debouncedUserFilter]);

  const handleProductLinkChange = (url) => {
    setProductLink(url);
    setItemError(null);
    const extracted = extractOfferId(url);
    if (extracted) {
      setExtractedItemNumber(extracted);
    } else {
      setExtractedItemNumber('');
    }
  };

  const handleFetchItem = async () => {
    const itemNumber = extractedItemNumber || extractOfferId(productLink);
    if (!itemNumber) {
      setItemError(t('cart:messages.invalidLink', 'Please enter a valid 1688 or Checkstall product link'));
      return;
    }

    try {
      setLoadingItem(true);
      setItemError(null);
      setItemData(null);
      setSelectedSKUs({});
      setSelectedProperties({});
      setSelectedFirstSpec(null);
      setTranslationTaskId(null);
      setIsPollingTranslation(false);
      setTranslationPollCount(0);
      
      // Fetch item from API
      const response = await fetchItemDetails(itemNumber);
      if (response && response.status === 'success' && response.item) {
        setItemData(response.item);
        
        // Start polling for translation if task ID is provided
        if (response.translation_task_id) {
          setTranslationTaskId(response.translation_task_id);
          setIsPollingTranslation(true);
          setTranslationPollCount(0);
        }
      } else {
        const errorMsg = response?.message || t('cart:messages.fetchFailed', 'Failed to fetch item details');
        setItemError(errorMsg);
      }
    } catch (error) {
      setItemError(error.response?.data?.message || error.response?.data?.error || t('cart:messages.fetchFailed', 'Failed to fetch item details'));
    } finally {
      setLoadingItem(false);
    }
  };

  // Poll for translation status
  useEffect(() => {
    if (!isPollingTranslation || !translationTaskId) {
      return;
    }

    // Stop polling if we've exceeded max attempts
    if (translationPollCount >= MAX_POLL_ATTEMPTS) {
      setIsPollingTranslation(false);
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await checkTranslationStatus(translationTaskId);
        
        if (statusResponse.status === 'success') {
          // Translation completed successfully
          setIsPollingTranslation(false);
          
          // Update itemData with translated props_list and skus
          setItemData(prevData => {
            if (!prevData) return prevData;
            
            const updatedData = { ...prevData };
            
            // Update props_list with translated version
            if (statusResponse.props_list) {
              updatedData.props_list = {
                ...prevData.props_list,
                ...statusResponse.props_list,
              };
            }
            
            // Update SKUs with translated properties_name
            const translatedSkus = statusResponse.skus || {};
            
            // Backend returns SKUs in format: {sku: [array of sku objects]}
            if (translatedSkus.sku && Array.isArray(translatedSkus.sku)) {
              // Create a map of translated SKUs by sku_id for quick lookup
              const translatedSkuMap = new Map();
              translatedSkus.sku.forEach((translatedSku) => {
                const skuId = String(translatedSku.sku_id || translatedSku.id || '');
                if (skuId && translatedSku.properties_name) {
                  translatedSkuMap.set(skuId, translatedSku.properties_name);
                }
              });
              
              // Update itemData.skus.sku if it exists (array format)
              if (updatedData.skus && updatedData.skus.sku && Array.isArray(updatedData.skus.sku)) {
                updatedData.skus = {
                  ...updatedData.skus,
                  sku: updatedData.skus.sku.map(sku => {
                    const skuId = String(sku.sku_id || sku.id || '');
                    if (translatedSkuMap.has(skuId)) {
                      return {
                        ...sku,
                        properties_name: translatedSkuMap.get(skuId),
                      };
                    }
                    return sku;
                  }),
                };
              }
              
              // Update itemData.sku if it exists (object format with keys as sku_id)
              if (updatedData.sku && typeof updatedData.sku === 'object' && !Array.isArray(updatedData.sku)) {
                const updatedSkuObject = {};
                Object.keys(updatedData.sku).forEach(skuId => {
                  const originalSku = updatedData.sku[skuId];
                  const skuIdStr = String(skuId);
                  // Try to match by the key (skuId) or by the sku_id/id in the object
                  const matchKey = skuIdStr;
                  const matchId = String(originalSku?.sku_id || originalSku?.id || '');
                  
                  if (translatedSkuMap.has(matchKey) || translatedSkuMap.has(matchId)) {
                    const translatedName = translatedSkuMap.get(matchKey) || translatedSkuMap.get(matchId);
                    updatedSkuObject[skuId] = {
                      ...originalSku,
                      properties_name: translatedName,
                    };
                  } else {
                    updatedSkuObject[skuId] = originalSku;
                  }
                });
                updatedData.sku = updatedSkuObject;
              }
              
              // Also handle if itemData.sku is an array
              if (updatedData.sku && Array.isArray(updatedData.sku)) {
                updatedData.sku = updatedData.sku.map(sku => {
                  const skuId = String(sku.sku_id || sku.id || '');
                  if (translatedSkuMap.has(skuId)) {
                    return {
                      ...sku,
                      properties_name: translatedSkuMap.get(skuId),
                    };
                  }
                  return sku;
                });
              }
            }
            
            return updatedData;
          });
          
          clearInterval(pollInterval);
        } else if (statusResponse.status === 'error') {
          // Translation failed - use original text
          setIsPollingTranslation(false);
          clearInterval(pollInterval);
        } else if (statusResponse.status === 'pending') {
          // Still pending - continue polling
          setTranslationPollCount(prev => prev + 1);
        }
      } catch (error) {
        // On error, stop polling and use original text
        setIsPollingTranslation(false);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [isPollingTranslation, translationTaskId, translationPollCount]);

  const handlePropertySelect = (propKey, propValue) => {
    const newProperties = { ...selectedProperties, [propKey]: propValue };
    setSelectedProperties(newProperties);
    
    if (itemData && itemData.sku) {
      const matchingSkuId = findMatchingSku(newProperties);
      if (matchingSkuId) {
        const sku = itemData.sku[matchingSkuId];
        if (sku) {
          setSelectedSKUs({
            [matchingSkuId]: {
              quantity: selectedSKUs[matchingSkuId]?.quantity || sku.quantity || 1,
              price: sku.price || itemData.price || 0,
              total_price: (selectedSKUs[matchingSkuId]?.quantity || sku.quantity || 1) * (sku.price || itemData.price || 0),
              properties: sku.properties || '',
              properties_name: sku.properties_name || '',
              url: sku.url || '',
            }
          });
        }
      }
    }
  };

  const findMatchingSku = (properties) => {
    if (!itemData || !itemData.sku) return null;
    
    const propString = Object.entries(properties)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}:${value}`)
      .join(';');
    
    for (const [skuId, sku] of Object.entries(itemData.sku)) {
      if (sku.properties === propString) {
        return skuId;
      }
    }
    
    return null;
  };

  const handleQuantityChange = (skuId, quantity) => {
    const sku = selectedSKUs[skuId];
    if (sku) {
      setSelectedSKUs({
        ...selectedSKUs,
        [skuId]: {
          ...sku,
          quantity: Math.max(1, parseInt(quantity) || 1),
          total_price: sku.price * Math.max(1, parseInt(quantity) || 1),
        }
      });
    }
  };

  const handleAddToCart = async () => {
    // Validation checks
    if (Object.keys(selectedSKUs).length === 0) {
      showSnackbar(t('cart:messages.selectSpecification', 'Please select at least one item specification'), 'error');
      return;
    }

    // Calculate total quantity for discount calculation
    const totalQuantity = Object.values(selectedSKUs).reduce((sum, skuData) => sum + (skuData.quantity || 0), 0);
    
    // Calculate discount info (matching frontend structure)
    const discountInfo = getItemDiscountInfo(itemData, totalQuantity);
    
    const cartItem = {
      num_iid: itemData.num_iid,
      title: itemData.title,
      price: itemData.price || 0,
      original_price: itemData.original_price || itemData.price || 0,
      nick: itemData.nick || '',
      min_num: itemData.min_num || 1,
      detail_url: itemData.detail_url || `https://detail.1688.com/offer/${itemData.num_iid}.html`,
      pic_url: itemData.pic_url || itemData.pic_urls?.[0] || '',
      post_fee: itemData.post_fee || 0,
      seller_id: itemData.seller_id || 0,
      priceRange: itemData.priceRange || [],
      seller_info: itemData.seller_info || {},
      discount_price: discountInfo.discountPrice,
      discount_info: discountInfo,
      sku: {},
    };

    Object.entries(selectedSKUs).forEach(([skuId, skuData]) => {
      // Get image URL - use from skuData, or find from SKU by ID, or use properties
      const imageUrl = skuData.url || getSkuImageUrlById(skuId) || getSkuImageUrl(skuData.properties || '') || itemData.pic_url || '';
      
      cartItem.sku[skuId] = {
        price: skuData.price,
        qty: skuData.quantity,
        total_price: skuData.total_price,
        sku_id: skuId,
        properties: skuData.properties,
        properties_name: skuData.properties_name,
        url: imageUrl,
      };
    });

    if (!userIdentifier.trim()) {
      setUserSearchError(t('cart:messages.enterUser', 'Please enter username or phone number'));
      return;
    }

    try {
      setLoadingItem(true);
      const response = await addItemToUserCart(cartItem, userIdentifier.trim());
      
      if (response.status === 'success') {
        const username = response.user?.username || response.user?.phone || 'user';
        showSnackbar(t('cart:messages.itemAdded', { username }, 'Item added to {{username}}\'s cart successfully'), 'success');
        handleCloseAddDialog();
        loadCartItems();
      } else {
        showSnackbar(response.message || t('cart:messages.itemAddFailed', 'Failed to add item to cart'), 'error');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t('cart:messages.itemAddFailed', 'Failed to add item to cart');
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoadingItem(false);
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialog(false);
    setProductLink('');
    setExtractedItemNumber('');
    setItemData(null);
    setSelectedSKUs({});
    setSelectedProperties({});
    setUserIdentifier('');
    setUserSearchError('');
    setItemError(null);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSkuQuantityInputChange = (skuId, value) => {
    if (!/^\d*$/.test(value)) {
      return;
    }
    setSkuQuantities(prev => ({ ...prev, [skuId]: value }));
  };

  const updateCartState = useCallback((updatedCart) => {
    setCartItems(prev => prev.map(item => (item.id === updatedCart.id ? updatedCart : item)));
    setSelectedCartItemForSkus(prev => (prev?.id === updatedCart.id ? updatedCart : prev));
    setSkuQuantities(buildSkuQuantitiesMap(updatedCart));
  }, [buildSkuQuantitiesMap]);

  const handleUpdateSkuQuantity = async (sku) => {
    const skuId = sku?.id;
    if (!skuId) {
      showSnackbar(t('cart:messages.updateSkuFailed', 'Failed to update SKU quantity'), 'error');
      return;
    }
    const inputValue = skuQuantities[skuId];
    const quantity = parseInt(inputValue, 10);
    if (!quantity || quantity < 1) {
      showSnackbar(t('cart:messages.invalidQuantity', 'Quantity must be greater than 0'), 'error');
      return;
    }
    try {
      setUpdatingSkuId(skuId);
      const response = await updateAdminCartSkuQuantity(skuId, quantity);
      if (response?.status === 'success' && response?.cart) {
        updateCartState(response.cart);
        showSnackbar(t('cart:messages.skuUpdated', 'SKU quantity updated successfully'), 'success');
        return;
      }
      showSnackbar(response?.message || t('cart:messages.updateSkuFailed', 'Failed to update SKU quantity'), 'error');
    } catch (error) {
      showSnackbar(error.response?.data?.message || t('cart:messages.updateSkuFailed', 'Failed to update SKU quantity'), 'error');
    } finally {
      setUpdatingSkuId(null);
    }
  };

  const handleDeleteCartItem = async () => {
    const cartId = selectedCartItemForSkus?.id;
    if (!cartId) {
      return;
    }
    try {
      setDeletingCartId(cartId);
      const response = await deleteAdminCartItem(cartId);
      if (response?.status === 'success') {
        setViewSkusDialog(false);
        setSelectedCartItemForSkus(null);
        setSkuQuantities({});
        setSelectedCartItems(prev => prev.filter(id => id !== cartId));
        showSnackbar(t('cart:messages.cartDeleted', 'Cart item deleted successfully'), 'success');
        await loadCartItems();
        return;
      }
      showSnackbar(response?.message || t('cart:messages.deleteCartFailed', 'Failed to delete cart item'), 'error');
    } catch (error) {
      showSnackbar(error.response?.data?.message || t('cart:messages.deleteCartFailed', 'Failed to delete cart item'), 'error');
    } finally {
      setDeletingCartId(null);
    }
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput('');
    } else {
      setRowsPerPage(parseInt(value, 10));
      setPage(0);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const clearFilters = () => {
    setUserFilter('');
  };

  const hasActiveFilters = userFilter.trim() !== '';

  // Add serial numbers
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  // Build columns for DataGrid
  const columns = [
    {
      field: 'select',
      headerName: '',
      width: 60,
      sortable: false,
      renderHeader: () => (
        <Checkbox
          checked={selectedCartItems.length > 0 && selectedCartItems.length === cartItems.length}
          indeterminate={selectedCartItems.length > 0 && selectedCartItems.length < cartItems.length}
          onChange={(e) => {
            if (e.target.checked) {
              const allIds = cartItems.map(item => item.id);
              setSelectedCartItems(allIds);
            } else {
              setSelectedCartItems([]);
            }
          }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selectedCartItems.includes(params.row.id)}
          onChange={() => {
            setSelectedCartItems(prev =>
              prev.includes(params.row.id)
                ? prev.filter(id => id !== params.row.id)
                : [...prev, params.row.id]
            );
          }}
        />
      )
    },
    {
      field: 'sn',
      headerName: '#',
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: 'pic_url',
      headerName: t('cart:table.image', 'Image'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Avatar
          src={params.value}
          variant="rounded"
          sx={{ width: 60, height: 60 }}
        >
          <ImageIcon />
        </Avatar>
      ),
    },
    {
      field: 'title',
      headerName: t('cart:table.product', 'Product'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'user',
      headerName: t('cart:table.user', 'User'),
      width: 200,
      renderCell: (params) => {
        const user = params.value;
        if (typeof user === 'object' && user) {
          return `${user.username || ''} (${user.phone || ''})`;
        }
        return params.value || 'N/A';
      },
    },
    {
      field: 'price',
      headerName: t('cart:table.price', 'Price'),
      width: 120,
      renderCell: (params) => formatPrice(params.value || 0),
    },
    {
      field: 'added_by_admin',
      headerName: t('cart:table.adminAdded', 'Admin Added'),
      width: 200,
      renderCell: (params) => {
        const admin = params.value;
        if (admin && typeof admin === 'object') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={admin.username || admin.email || 'N/A'}
                color="primary"
                size="small"
              />
            </Box>
          );
        }
        return (
          <Chip
            label="No"
            color="default"
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: t('cart:table.addedAt', 'Added At'),
      width: 180,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        return date.toLocaleString();
      },
    },
    {
      field: 'actions',
      headerName: t('cart:table.actions', 'Actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="primary"
          onClick={() => {
            setSelectedCartItemForSkus(params.row);
            setSkuQuantities(buildSkuQuantitiesMap(params.row));
            setUpdatingSkuId(null);
            setDeletingCartId(null);
            setViewSkusDialog(true);
          }}
          title={t('cart:actions.viewSkus', 'View SKUs')}
        >
          <Visibility />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('cart:title', 'Cart Management')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddDialog(true)}
          sx={{ 
            backgroundColor: '#DC143C', 
            '&:hover': { backgroundColor: '#B91C3C' },
            minWidth: 'auto'
          }}
        >
          {t('cart:addItem', 'Add Item to Cart')}
        </Button>
      </Box>

      {/* Filter Section */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold' }}>
              {t('cart:filter.title', 'Filter Cart Items')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                sx={{ borderColor: '#DC143C', color: '#DC143C' }}
              >
                {t('cart:filter.clearFilters', 'Clear Filters')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadCartItems}
                disabled={loading}
                sx={{ minWidth: 'auto' }}
              >
                {t('common:buttons.refresh')}
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                label={t('cart:filter.userLabel', 'Filter by User')}
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                placeholder={t('cart:filter.userPlaceholder', 'Enter username or phone')}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Cart Items Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {cartItems.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('common:table.noData')}
                </Typography>
                <Button variant="outlined" onClick={loadCartItems}>
                  {t('common:buttons.refresh')}
                </Button>
              </Box>
            ) : (
              <>
                <DataGrid
                  rows={addSerialNumbers(cartItems, page, rowsPerPage)}
                  columns={columns}
                  getRowId={(row) => row.id}
                  loading={loading}
                  hideFooter
                  disableSelectionOnClick
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  autoHeight={false}
                  sx={{
                    border: 'none',
                    minWidth: 1000,
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: '#DC143C',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    },
                    '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                      color: 'white'
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#FFF0F3'
                    },
                    '& .MuiDataGrid-cell': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      padding: { xs: '8px 4px', sm: '8px 12px' }
                    },
                    '& .MuiDataGrid-columnSeparator': {
                      color: 'white'
                    }
                  }}
                />
              </>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Checkout Button - Show when items are selected (Outside Card for visibility) */}
      {selectedCartItems && Array.isArray(selectedCartItems) && selectedCartItems.length > 0 && (
        <Card sx={{ mt: 2, backgroundColor: '#f5f5f5', border: '2px solid #DC143C', boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              gap: 2,
              alignItems: 'center'
            }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                {t('cart:selectedCount', '{{count}} item(s) selected', { count: selectedCartItems.length })}
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<ShoppingBag />}
                  onClick={async () => {
                  // Navigate to checkout with selected cart item IDs
                  const selectedIds = selectedCartItems.join(',');
                  const prefetchedSettings = checkoutPricingSettings || await getCheckoutPricingSettings();
                  navigate(`/admin/cart/checkout?items=${selectedIds}`, {
                    state: {
                      prefetchedShippingSettings: prefetchedSettings
                    }
                  });
                }}
                sx={{ 
                  backgroundColor: '#DC143C', 
                  '&:hover': { backgroundColor: '#B91C3C' },
                  minWidth: 200,
                  py: 1.5
                }}
              >
                {t('cart:proceedToCheckout', 'Proceed to Checkout')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Custom Pagination Bar */}
      {cartItems.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: { xs: 'center', sm: 'flex-end' }, 
          mt: 3, 
          gap: 2,
          flexWrap: 'wrap'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePrevPage} disabled={page === 0}>
              <ChevronLeft />
            </IconButton>
            <Typography variant="body2">
              {t('cart:pagination.page', 'Page')} {totalPages === 0 ? 0 : page + 1} {t('cart:pagination.of', 'of')} {totalPages}
            </Typography>
            <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
              <ChevronRight />
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {t('cart:pagination.rowsPerPage', 'Rows per page')}
            </Typography>
            {!customRowsActive ? (
              <TextField
                select
                size="small"
                value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
                onChange={handleRowsPerPageChange}
                sx={{ width: 100 }}
              >
                {[10, 20, 50, 100].map((option) => (
                  <MuiMenuItem key={option} value={option}>{option}</MuiMenuItem>
                ))}
                <MuiMenuItem value="custom">{t('cart:pagination.custom', 'Custom')}</MuiMenuItem>
              </TextField>
            ) : (
              <TextField
                size="small"
                value={customRowsInput}
                onChange={handleCustomRowsInputChange}
                onBlur={handleCustomRowsInputBlur}
                autoFocus
                sx={{ width: 100 }}
                inputProps={{ min: 1, style: { textAlign: 'center' } }}
                placeholder={t('cart:pagination.rows', 'Rows')}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Add Item Dialog */}
      <Dialog
        open={addDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('cart:dialog.title', 'Add Item to User Cart')}</Typography>
            <IconButton onClick={handleCloseAddDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Product Link Input */}
            <TextField
              fullWidth
              label={t('cart:dialog.productLink', 'Product Link (1688 or Checkstall)')}
              value={productLink}
              onChange={(e) => handleProductLinkChange(e.target.value)}
              placeholder={t('cart:dialog.productLinkPlaceholder', 'Paste 1688 or Checkstall product link here...')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
              }}
            />
            {extractedItemNumber && (
              <Alert severity="info">
                {t('cart:dialog.extractedItemNumber', 'Extracted Item Number')}: <strong>{extractedItemNumber}</strong>
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={handleFetchItem}
              disabled={!extractedItemNumber || loadingItem}
              startIcon={loadingItem ? <CircularProgress size={20} /> : <Search />}
              sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
            >
              {loadingItem ? t('common:buttons.loading') : t('cart:dialog.fetchProduct', 'Fetch Product Details')}
            </Button>

            {itemError && (
              <Alert severity="error">{itemError}</Alert>
            )}

            {/* Product Preview */}
            {itemData && (
              <>
                <Divider />
                <Typography variant="h6">{t('cart:dialog.productPreview', 'Product Preview')}</Typography>
                <Card>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Avatar
                          src={
                            (() => {
                              const propsImg = itemData?.prop_imgs?.prop_img || [];
                              // For 2-spec items: find image by property, for 1-spec: use imageIndex
                              if (property && itemData?.props_list?.[property]) {
                                // Check if this is a 2-spec item by checking if property exists in props_list
                                const matchingImg = propsImg.find(img => img.properties === property);
                                return matchingImg?.url || itemData.pic_url || itemData.pic_urls?.[0];
                              } else {
                                // For 1-spec items: use imageIndex
                                return propsImg[imageIndex]?.url || itemData.pic_url || itemData.pic_urls?.[0];
                              }
                            })()
                          }
                          variant="rounded"
                          sx={{ width: '100%', height: 200 }}
                        >
                          <ImageIcon sx={{ fontSize: 60 }} />
                        </Avatar>
                      </Grid>
                      <Grid size={{ xs: 12, md: 8 }}>
                        <Typography variant="h6" gutterBottom>
                          {itemData.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('cart:table.id', 'ID')}: {itemData.num_iid}
                        </Typography>
                        <Typography variant="h6" color="primary" gutterBottom>
                          {t('cart:table.price', 'Price')}: {formatPrice(itemData.price || 0)}
                        </Typography>
                        {itemData.min_num && (
                          <Chip label={`MOQ: ${itemData.min_num}`} size="small" sx={{ mt: 1 }} />
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Specifications Selector - Conditional rendering based on specs count */}
                
                {itemData && (() => {
                  // Hardcoded: Determine if item has 2 specs (for testing, always show 2-spec UI)

                  const getProperties = (item, props) => {
                    const propsList = item?.props_list;
                    return propsList?.[props] ? propsList[props]?.split(':')[0] : "";
                  };
                  const hasTwoSpecs = getProperties(itemData, "1:0");
                  const spec_1_title = getProperties(itemData, "0:0");
                  const spec_2_title = getProperties(itemData, "1:0");
                  const propsImg = itemData?.prop_imgs?.prop_img || [];
                  
                  const getPropsList = (item) => {
                    return item ? item?.props_list || {} : {};
                  }
                  
                  // Extract first spec options dynamically
                  const firstSpecOptions = [];
                  Object.entries(getPropsList(itemData)).forEach((val, index) => {
                    let key = val[0].split(":")[0];
                    if (parseInt(key) === 0) {
                      const fullKey = val[0]; // e.g., "0:0"
                      const fullValue = val[1]; // e.g., "颜色:多款多式混合发货"
                      const specValue = fullValue.split(':')[1] || fullValue; // Get value after colon
                      
                      // Find matching image from propsImg
                      const matchingImg = propsImg.find(img => img.properties === fullKey);
                      const imageUrl = matchingImg?.url || itemData.pic_url;
                      
                      firstSpecOptions.push({
                        value: specValue,
                        image: imageUrl,
                        fullKey: fullKey, // Store the property key (e.g., "0:0")
                        selected: selectedFirstSpec === specValue,
                      });
                    }
                  });
                  
                  return (
                    <>
                      <Divider sx={{ my: 3 }} />
                      
                      {hasTwoSpecs ? (
                        /* ========== 2-SPEC UI (for items with 2 specs) ========== */
                        <Stack spacing={3}>
                          {/* First Spec Section - Static hardcoded data */}
                          <Box>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1rem' }}>
                              {spec_1_title}
                              {isPollingTranslation && (
                                <Chip 
                                  label="Translating..." 
                                  size="small" 
                                  color="info" 
                                  sx={{ ml: 2, fontSize: '0.75rem' }}
                                />
                              )}
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap',
                              gap: 2,
                            }}>
                              {/* Show skeleton loaders while translation is in progress */}
                              {isPollingTranslation ? (
                                // Skeleton loaders for first spec
                                Array.from({ length: 3 }).map((_, index) => (
                                  <Box
                                    key={`skeleton-1-${index}`}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                      p: 1.5,
                                      minWidth: 200,
                                      flex: '1 1 auto',
                                      maxWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' },
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 1,
                                      backgroundColor: 'white',
                                    }}
                                  >
                                    <Skeleton variant="rounded" width={50} height={50} />
                                    <Skeleton variant="text" width={120} height={20} />
                                  </Box>
                                ))
                              ) : (
                                /* Static First Spec Options */
                                firstSpecOptions.map((option, index) => (
                                  <Box
                                    key={option.value}
                                    onClick={() => handleVariationClick(index, option.fullKey)}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                      p: 1.5,
                                      minWidth: 200,
                                      flex: '1 1 auto',
                                      maxWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' },
                                      border: option.selected ? '2px solid #DC143C' : '1px solid #e0e0e0',
                                      borderRadius: 1,
                                      cursor: 'pointer',
                                      backgroundColor: option.selected ? '#FFF5F0' : 'white',
                                      '&:hover': {
                                        borderColor: '#DC143C',
                                        backgroundColor: '#FFF5F0',
                                      },
                                    }}
                                  >
                                    {/* Image thumbnail */}
                                    <Avatar
                                      src={option.image}
                                      variant="rounded"
                                      sx={{ 
                                        width: 50, 
                                        height: 50,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <ImageIcon />
                                    </Avatar>
                                    
                                    {/* Text */}
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontSize: '0.875rem',
                                        fontWeight: option.selected ? 500 : 400,
                                        flex: 1,
                                      }}
                                    >
                                      {option.value}
                                    </Typography>
                                  </Box>
                                ))
                              )}
                            </Box>
                          </Box>

                          {/* Second Spec Section - Static hardcoded data */}
                          <Box>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1rem' }}>
                              {spec_2_title}
                              {isPollingTranslation && (
                                <Chip 
                                  label="Translating..." 
                                  size="small" 
                                  color="info" 
                                  sx={{ ml: 2, fontSize: '0.75rem' }}
                                />
                              )}
                            </Typography>
                            <Stack spacing={1.5}>
                              {/* Show skeleton loaders while translation is in progress */}
                              {isPollingTranslation ? (
                                // Skeleton loaders for second spec
                                Array.from({ length: 5 }).map((_, index) => (
                                  <Box
                                    key={`skeleton-2-${index}`}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      p: 1.5,
                                      border: '1px solid #f0f0f0',
                                      borderRadius: 1,
                                      backgroundColor: '#fafafa',
                                    }}
                                  >
                                    <Skeleton variant="text" width={150} height={20} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                        <Skeleton variant="text" width={80} height={20} />
                                        <Skeleton variant="text" width={60} height={16} />
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Skeleton variant="rounded" width={32} height={32} />
                                        <Skeleton variant="rounded" width={50} height={32} />
                                        <Skeleton variant="rounded" width={32} height={32} />
                                      </Box>
                                    </Box>
                                  </Box>
                                ))
                              ) : (
                                /* Static Second Spec Options */
                                secondSpecOptions.map((option) => {
                                const currentQuantity = selectedSKUs[option.skuId]?.quantity || 0;
                                
                                return (
                                  <Box
                                    key={option.skuId}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      p: 1.5,
                                      border: '1px solid #f0f0f0',
                                      borderRadius: 1,
                                      backgroundColor: '#fafafa',
                                    }}
                                  >
                                    {/* Left side: Text only */}
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {option.value}
                                      </Typography>
                                    </Box>
                                    
                                    {/* Right side: Price, Stock, Quantity Controls */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      {/* Price and Stock */}
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                          {formatPrice(option.price)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                          Stock: {option.stock}
                                        </Typography>
                                      </Box>
                                      
                                      {/* Quantity Controls */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const newQty = Math.max(0, currentQuantity - 1);
                                            if (newQty === 0) {
                                              const { [option.skuId]: removed, ...rest } = selectedSKUs;
                                              setSelectedSKUs(rest);
                                                } else {
                                                  setSelectedSKUs({
                                                    ...selectedSKUs,
                                                    [option.skuId]: {
                                                      quantity: newQty,
                                                      price: option.price,
                                                      total_price: option.price * newQty,
                                                      properties: option.properties,
                                                      properties_name: option.properties_name,
                                                      url: option.imageUrl || getSkuImageUrlById(option.skuId) || getSkuImageUrl(option.properties),
                                                    },
                                                  });
                                                }
                                          }}
                                          disabled={currentQuantity === 0}
                                          sx={{
                                            minWidth: 32,
                                            width: 32,
                                            height: 32,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            backgroundColor: 'white',
                                            '&:hover': {
                                              backgroundColor: '#f5f5f5',
                                            },
                                            '&:disabled': {
                                              backgroundColor: '#fafafa',
                                              borderColor: '#e0e0e0',
                                            },
                                          }}
                                        >
                                          <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>-</Typography>
                                        </IconButton>
                                        
                                        <TextField
                                          value={currentQuantity}
                                          onChange={(e) => {
                                            const qty = Math.max(0, parseInt(e.target.value) || 0);
                                            if (qty === 0) {
                                              const { [option.skuId]: removed, ...rest } = selectedSKUs;
                                              setSelectedSKUs(rest);
                                            } else {
                                              setSelectedSKUs({
                                                ...selectedSKUs,
                                                [option.skuId]: {
                                                  quantity: qty,
                                                  price: option.price,
                                                  total_price: option.price * qty,
                                                  properties: option.properties,
                                                  properties_name: option.properties_name,
                                                  url: option.imageUrl || getSkuImageUrl(option.properties),
                                                },
                                              });
                                            }
                                          }}
                                          inputProps={{
                                            min: 0,
                                            style: { 
                                              textAlign: 'center', 
                                              padding: '4px 8px',
                                              fontSize: '0.875rem',
                                            },
                                          }}
                                          size="small"
                                          sx={{ 
                                            width: 50,
                                            '& .MuiOutlinedInput-root': {
                                              height: 32,
                                              backgroundColor: 'white',
                                            },
                                          }}
                                        />
                                        
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const newQty = currentQuantity + 1;
                                              setSelectedSKUs({
                                                ...selectedSKUs,
                                                [option.skuId]: {
                                                  quantity: newQty,
                                                  price: option.price,
                                                  total_price: option.price * newQty,
                                                  properties: option.properties || '',
                                                  properties_name: option.properties_name || option.value,
                                                  url: option.imageUrl || getSkuImageUrlById(option.skuId) || getSkuImageUrl(option.properties || ''),
                                                },
                                              });
                                          }}
                                          disabled={option.stock <= currentQuantity}
                                          sx={{
                                            minWidth: 32,
                                            width: 32,
                                            height: 32,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            backgroundColor: 'white',
                                            '&:hover': {
                                              backgroundColor: '#f5f5f5',
                                            },
                                            '&:disabled': {
                                              backgroundColor: '#fafafa',
                                              borderColor: '#e0e0e0',
                                            },
                                          }}
                                        >
                                          <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>+</Typography>
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  </Box>
                                );
                              })
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      ) : (
                        /* ========== 1-SPEC UI (for items with 1 spec) - Dynamic from SKU ========== */
                        <Box>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1rem' }}>
                            {spec_1_title}
                            {isPollingTranslation && (
                              <Chip 
                                label="Translating..." 
                                size="small" 
                                color="info" 
                                sx={{ ml: 2, fontSize: '0.75rem' }}
                              />
                            )}
                          </Typography>
                          <Stack spacing={1.5}>
                            {/* Show skeleton loaders while translation is in progress */}
                            {isPollingTranslation ? (
                              // Skeleton loaders for 1-spec items
                              Array.from({ length: 5 }).map((_, index) => (
                                <Box
                                  key={`skeleton-1spec-${index}`}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 1.5,
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 1,
                                    backgroundColor: 'white',
                                  }}
                                >
                                  <Skeleton variant="rounded" width={50} height={50} />
                                  <Skeleton variant="text" width={150} height={20} sx={{ flex: 1 }} />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Skeleton variant="text" width={50} height={20} />
                                    <Skeleton variant="text" width={80} height={16} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Skeleton variant="rounded" width={32} height={32} />
                                      <Skeleton variant="rounded" width={50} height={32} />
                                      <Skeleton variant="rounded" width={32} height={32} />
                                    </Box>
                                  </Box>
                                </Box>
                              ))
                            ) : (
                              /* Dynamic 1-Spec Options from SKU */
                              sku.map((val, index) => {
                              let props_1 = val.properties?.split(";")[0];
                              // For 1-spec items, show all SKUs where props_1 starts with "0:" (first spec)
                              // For 2-spec items, this would still work as property is "0:0"
                              if (props_1 && props_1.startsWith(property.split(":")[0] + ":")) {
                                // Use formatPropertiesName to extract the formatted spec value (handles all cases)
                                const specValue = formatPropertiesName(val.properties_name);
                                
                                // Find matching image from propsImg using the full property key
                                const matchingImg = propsImg.find(img => img.properties === props_1);
                                const imageUrl = matchingImg?.url || itemData.pic_url;
                                
                                const currentQuantity = selectedSKUs[val.sku_id?.toString() || val.sku_id]?.quantity || 0;
                                const skuId = val.sku_id?.toString() || val.sku_id || '';
                                
                                return (
                                  <Box
                                    key={skuId || index}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      p: 1.5,
                                      border: '1px solid #f0f0f0',
                                      borderRadius: 1,
                                      backgroundColor: 'white',
                                    }}
                                  >
                                    {/* Left: Icon/Thumbnail - Clickable for 1-spec items */}
                                    <Avatar
                                      src={imageUrl}
                                      variant="rounded"
                                      onClick={() => handleVariationClick(index)}
                                      sx={{ 
                                        width: 50, 
                                        height: 50,
                                        flexShrink: 0,
                                        cursor: 'pointer',
                                        '&:hover': {
                                          opacity: 0.8,
                                        },
                                      }}
                                    >
                                      <ImageIcon />
                                    </Avatar>
                                    
                                    {/* Middle: Spec Name */}
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
                                        {specValue}
                                      </Typography>
                                    </Box>
                                    
                                    {/* Right: Price, Stock, Quantity Controls */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      {/* Price */}
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, minWidth: 50 }}>
                                        {formatPrice(val.price || 0)}
                                      </Typography>
                                      
                                      {/* Stock */}
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 80 }}>
                                        Stock: {val.quantity || 0}
                                      </Typography>
                                      
                                      {/* Quantity Controls */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const newQty = Math.max(0, currentQuantity - 1);
                                            if (newQty === 0) {
                                              const { [skuId]: removed, ...rest } = selectedSKUs;
                                              setSelectedSKUs(rest);
                                            } else {
                                              setSelectedSKUs({
                                                ...selectedSKUs,
                                                [skuId]: {
                                                  quantity: newQty,
                                                  price: parseFloat(val.price) || 0,
                                                  total_price: (parseFloat(val.price) || 0) * newQty,
                                                  properties: val.properties || '',
                                                  properties_name: val.properties_name || '',
                                                  url: getSkuImageUrl(val.properties || ''),
                                                },
                                              });
                                            }
                                          }}
                                          disabled={currentQuantity === 0}
                                          sx={{
                                            minWidth: 32,
                                            width: 32,
                                            height: 32,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            backgroundColor: 'white',
                                            '&:hover': {
                                              backgroundColor: '#f5f5f5',
                                            },
                                            '&:disabled': {
                                              backgroundColor: '#fafafa',
                                              borderColor: '#e0e0e0',
                                            },
                                          }}
                                        >
                                          <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>-</Typography>
                                        </IconButton>
                                        
                                        <TextField
                                          value={currentQuantity}
                                          onChange={(e) => {
                                            const qty = Math.max(0, parseInt(e.target.value) || 0);
                                            if (qty === 0) {
                                              const { [skuId]: removed, ...rest } = selectedSKUs;
                                              setSelectedSKUs(rest);
                                            } else {
                                              setSelectedSKUs({
                                                ...selectedSKUs,
                                                [skuId]: {
                                                  quantity: qty,
                                                  price: parseFloat(val.price) || 0,
                                                  total_price: (parseFloat(val.price) || 0) * qty,
                                                  properties: val.properties || '',
                                                  properties_name: val.properties_name || '',
                                                  url: '',
                                                },
                                              });
                                            }
                                          }}
                                          inputProps={{
                                            min: 0,
                                            style: { 
                                              textAlign: 'center', 
                                              padding: '4px 8px',
                                              fontSize: '0.875rem',
                                            },
                                          }}
                                          size="small"
                                          sx={{ 
                                            width: 50,
                                            '& .MuiOutlinedInput-root': {
                                              height: 32,
                                              backgroundColor: 'white',
                                            },
                                          }}
                                        />
                                        
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const newQty = currentQuantity + 1;
                                            setSelectedSKUs({
                                              ...selectedSKUs,
                                              [skuId]: {
                                                quantity: newQty,
                                                price: parseFloat(val.price) || 0,
                                                total_price: (parseFloat(val.price) || 0) * newQty,
                                                properties: val.properties || '',
                                                properties_name: val.properties_name || '',
                                                url: '',
                                              },
                                            });
                                          }}
                                          disabled={(val.quantity || 0) <= currentQuantity}
                                          sx={{
                                            minWidth: 32,
                                            width: 32,
                                            height: 32,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            backgroundColor: 'white',
                                            '&:hover': {
                                              backgroundColor: '#f5f5f5',
                                            },
                                            '&:disabled': {
                                              backgroundColor: '#fafafa',
                                              borderColor: '#e0e0e0',
                                            },
                                          }}
                                        >
                                          <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>+</Typography>
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  </Box>
                                );
                              }
                              return null;
                            })
                            )}
                          </Stack>
                        </Box>
                      )}
                    </>
                  );
                })()}

                {/* Selected Items Summary */}
                {(() => {
                  const totalItems = Object.values(selectedSKUs).reduce((sum, skuData) => sum + (skuData.quantity || 0), 0);
                  const totalPrice = Object.values(selectedSKUs).reduce((sum, skuData) => sum + (parseFloat(skuData.total_price) || 0), 0);
                  
                  return totalItems > 0 ? (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 2,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {t('cart:dialog.totalSelected', 'Total selected')}: {totalItems}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {t('cart:dialog.totalPrice', 'Total price')}: {formatPrice(totalPrice)}
                        </Typography>
                      </Box>
                    </>
                  ) : null;
                })()}

                {/* User Search */}
                <Divider />
                <Typography variant="h6">{t('cart:dialog.selectUser', 'Select User')}</Typography>
                <TextField
                  fullWidth
                  label={t('cart:dialog.userPlaceholder', 'Enter exact username or phone number')}
                  value={userIdentifier}
                  onChange={(e) => {
                    setUserIdentifier(e.target.value);
                    setUserSearchError('');
                  }}
                  error={!!userSearchError}
                  helperText={userSearchError || t('cart:dialog.userPlaceholder', 'Enter exact username or phone number')}
                  placeholder={t('cart:dialog.userPlaceholder', 'Enter exact username or phone number')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>{t('cart:dialog.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleAddToCart}
            disabled={!itemData || Object.keys(selectedSKUs).length === 0}
            startIcon={<ShoppingCart />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('cart:dialog.addToCart', 'Add to Cart')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View SKUs Dialog */}
      <Dialog
        open={viewSkusDialog}
        onClose={() => {
          setViewSkusDialog(false);
          setSelectedCartItemForSkus(null);
          setSkuQuantities({});
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('cart:dialog.viewSkus', 'View SKUs')} - {selectedCartItemForSkus?.title || ''}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setViewSkusDialog(false);
                setSelectedCartItemForSkus(null);
                setSkuQuantities({});
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCartItemForSkus && (() => {
            const skuArray = getSkuArray(selectedCartItemForSkus);
            
            if (skuArray.length === 0) {
              return (
                <Alert severity="info">
                  {t('cart:dialog.noSkus', 'No SKUs found for this cart item.')}
                </Alert>
              );
            }
            
            // Calculate totals
            const totalQuantity = skuArray.reduce((sum, sku) => sum + (parseInt(sku.quantity) || 0), 0);
            const totalPrice = skuArray.reduce((sum, sku) => sum + (parseFloat(sku.total_price) || 0), 0);
            
            return (
              <Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('cart:table.image', 'Image')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('cart:dialog.specification', 'Specification')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('cart:table.price', 'Price')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('cart:dialog.quantity', 'Quantity')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('cart:dialog.totalPrice', 'Total Price')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('cart:table.actions', 'Actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {skuArray.map((sku, index) => (
                        <TableRow key={sku.id || sku.sku_id || index}>
                          <TableCell>
                            <Avatar
                              src={sku.url || selectedCartItemForSkus.pic_url}
                              variant="rounded"
                              sx={{ width: 60, height: 60 }}
                            >
                              <ImageIcon />
                            </Avatar>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {(() => {
                                const propsName = sku.properties_name || '';
                                if (!propsName) return t('cart:dialog.noSpec', 'No specification');
                                
                                // Remove the index prefix (e.g., "0:0:" or "1:3:") from the beginning
                                // Format: "0:0: Color: M10 black [TFT screen + dual payment]"
                                // Result: "Color: M10 black [TFT screen + dual payment]"
                                const cleaned = propsName.replace(/^\d+:\d+:\s*/, '');
                                return cleaned || propsName;
                              })()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatPrice(parseFloat(sku.price) || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                              <TextField
                                size="small"
                                value={skuQuantities[sku.id || sku.sku_id] ?? String(sku.quantity || 1)}
                                onChange={(event) => handleSkuQuantityInputChange(sku.id || sku.sku_id, event.target.value)}
                                sx={{ width: 88 }}
                                inputProps={{ min: 1, inputMode: 'numeric' }}
                              />
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleUpdateSkuQuantity(sku)}
                                disabled={!sku.id || updatingSkuId === sku.id || deletingCartId === selectedCartItemForSkus?.id}
                              >
                                {updatingSkuId === sku.id ? t('common:buttons.loading', 'Saving...') : t('common:buttons.save', 'Save')}
                              </Button>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {formatPrice(parseFloat(sku.total_price) || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {sku.sku_id || sku.id}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {t('cart:dialog.total', 'Total')}:
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {totalQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {formatPrice(totalPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            startIcon={deletingCartId === selectedCartItemForSkus?.id ? <CircularProgress size={16} /> : <Delete />}
            onClick={handleDeleteCartItem}
            disabled={deletingCartId === selectedCartItemForSkus?.id || updatingSkuId !== null}
          >
            {deletingCartId === selectedCartItemForSkus?.id ? t('common:buttons.deleting', 'Deleting...') : t('cart:actions.deleteCartItem', 'Delete Cart Item')}
          </Button>
          <Button onClick={() => {
            setViewSkusDialog(false);
            setSelectedCartItemForSkus(null);
            setSkuQuantities({});
          }}>
            {t('common:buttons.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Cart;
